import { useEffect, useState } from "react";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { WorkOrderFilterBar } from "./ui/WorkOrderFilterBar";
import { WorkOrderList } from "./ui/WorkOrderList";
import { useWorkOrderFilters } from "../../../features/work-order";

function formatSecondsAgo(seconds: number): string {
  if (seconds < 60) return `Vor ${seconds} Sek.`;
  const mins = Math.floor(seconds / 60);
  return `Vor ${mins} Min.`;
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={spinning ? "animate-spin" : ""}
      style={{ animationDirection: "reverse" }}
    >
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

export const WorkOrderPage = () => {
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [secondsAgo, setSecondsAgo] = useState<number | null>(null);

  const { dataUpdatedAt, refetch, isFetching } = useWorkOrderFilters();

  useEffect(() => {
    if (dataUpdatedAt) setLastSyncAt(new Date(dataUpdatedAt));
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (!lastSyncAt) return;
    const tick = () =>
      setSecondsAgo(Math.floor((Date.now() - lastSyncAt.getTime()) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastSyncAt]);

  function handleSync() {
    refetch();
  }

  return (
    <PageLayout
      pageTitle="Reparaturaufträge"
      headerChildren={
        <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[15px] font-medium text-[#004494] shadow-sm">
          {/* Status dot */}
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
            <span className="font-semibold">Verbunden</span>
          </span>

          {/* Separator */}
          <span className="h-4 w-px bg-gray-300" />

          {/* Last sync time */}
          <span className="text-gray-500">
            {secondsAgo === null
              ? "Noch nicht aktualisiert"
              : `${formatSecondsAgo(secondsAgo)} aktualisiert`}
          </span>

          {/* Refresh button */}
          <button
            onClick={handleSync}
            disabled={isFetching}
            className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#E6F0FB] text-[#004494] transition hover:brightness-95 disabled:opacity-60"
            title="Aktualisieren"
          >
            <RefreshIcon spinning={isFetching} />
          </button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-4">
        <WorkOrderFilterBar />
        <WorkOrderList />
      </div>
    </PageLayout>
  );
};
